import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiLogIn,
  FiMail,
  FiLock,
  FiArrowLeft,
  FiHelpCircle,
  FiShield,
  FiTrendingUp,
  FiClock,
  FiKey,
} from 'react-icons/fi';
import { loginUser } from '../firebase/authService';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';

const highlights = [
  {
    icon: <FiShield className="text-cyan-300" />,
    title: 'Institutional security',
    description: 'Multi-layer protection and SOC2-ready infrastructure keep capital safe.',
  },
  {
    icon: <FiTrendingUp className="text-emerald-300" />,
    title: 'Signal-driven execution',
    description: 'AI surfaces high-conviction trades with real-time risk controls.',
  },
  {
    icon: <FiClock className="text-blue-300" />,
    title: '24/7 monitoring',
    description: 'Global desks watch the market so you can focus on strategy.',
  },
];

const SignInPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetFeedback, setResetFeedback] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await loginUser(email, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        throw new Error(result.error || 'Login failed. Please try again');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    setResetFeedback('');

    const trimmedEmail = (resetEmail || email).trim();
    if (!trimmedEmail) {
      setResetFeedback('Enter the email linked to your account.');
      return;
    }

    setIsSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, trimmedEmail);
      setResetFeedback(`Password reset email sent to ${trimmedEmail}. Check your inbox and spam folder.`);
    } catch (resetError) {
      console.error('Reset link error:', resetError);
      let message = 'Unable to send reset link. Please try again shortly.';
      switch (resetError.code) {
        case 'auth/invalid-email':
          message = 'Enter a valid email address.';
          break;
        case 'auth/user-not-found':
          message = 'No account exists for that email.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Please wait before requesting another link.';
          break;
        default:
          message = resetError.message || message;
      }
      setResetFeedback(message);
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-10 sm:px-6 lg:flex-row lg:items-stretch lg:py-16">
        <div className="flex flex-1 flex-col justify-between rounded-3xl border border-slate-800/80 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 p-10 text-slate-100 shadow-[0_35px_120px_-35px_RGBA(14,165,233,0.45)]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-3 rounded-full border border-slate-700/60 bg-slate-900/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-300/90">
              <span className="h-2 w-2 rounded-full bg-cyan-400" />
              Secure login portal
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-50 sm:text-5xl">
              Welcome back to Gemini AI Trading
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-slate-300">
              Authorize access to your trading cockpit, monitor live P&L, and configure automation in one place.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {highlights.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-800/80 bg-slate-900/70 p-4 shadow-inner shadow-slate-900/30">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800/80">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{item.title}</p>
                    <p className="text-xs text-slate-400">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />SOC2-ready controls
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-cyan-400" /> Dedicated support desk
            </span>
          </div>
        </div>

        <div className="mt-10 w-full lg:mt-0 lg:ml-10 lg:w-[420px]">
          <div className="h-full rounded-3xl border border-slate-800/80 bg-slate-900/80 p-8 shadow-[0_25px_60px_-25px_RGBA(15,118,110,0.35)] backdrop-blur-xl">
            <div className="text-center">
              <img
                src="/logo.png"
                alt="Gemini AI Trading"
                className="mx-auto mb-6 h-16 w-16"
              />
              <h2 className="text-2xl font-semibold text-slate-50">Secure sign in</h2>
              <p className="mt-1 text-sm text-slate-400">Access your analytics and automation suite</p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleLogin}>
              <div className="space-y-1">
                <label htmlFor="email" className="text-sm font-medium text-slate-300">
                  Email address
                </label>
                <div className="relative">
                  <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    className="w-full rounded-2xl border border-slate-700/70 bg-slate-950/70 py-3 pl-12 pr-4 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="password" className="text-sm font-medium text-slate-300">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    type="password"
                    className="w-full rounded-2xl border border-slate-700/70 bg-slate-950/70 py-3 pl-12 pr-4 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setIsResetMode((previous) => !previous);
                  setResetFeedback('');
                  setResetEmail(email);
                }}
                className="text-sm font-semibold text-cyan-300 transition hover:text-cyan-200"
              >
                {isResetMode ? 'Close password help' : 'Forgot password?'}
              </button>

              {isResetMode && (
                <form className="space-y-3 rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4" onSubmit={handlePasswordReset}>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <FiKey />
                    Reset your password
                  </div>
                  <p className="text-xs text-slate-400">
                    Receive an email with a secure link to pick a new password. The link expires after 15 minutes.
                  </p>
                  <div>
                    <label htmlFor="resetEmail" className="mb-1 block text-xs uppercase tracking-[0.2em] text-slate-500">
                      Email address
                    </label>
                    <div className="relative">
                      <FiMail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        id="resetEmail"
                        type="email"
                        value={resetEmail || email}
                        onChange={(event) => setResetEmail(event.target.value)}
                        className="w-full rounded-xl border border-slate-800/80 bg-slate-900/80 py-2 pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
                        placeholder="name@company.com"
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isSendingReset}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-400 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSendingReset ? 'Sending…' : 'Send reset link'}
                  </button>
                  {resetFeedback && (
                    <p className="text-xs font-medium text-slate-300">{resetFeedback}</p>
                  )}
                </form>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400 py-3 text-sm font-bold text-slate-900 shadow-[0_0_30px_rgba(34,211,238,0.5),0_0_60px_rgba(16,185,129,0.3)] transition-all hover:shadow-[0_0_40px_rgba(34,211,238,0.7),0_0_80px_rgba(16,185,129,0.5)] hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 animate-pulse"
              >
                <FiLogIn className="text-lg" /> {isLoading ? 'Authenticating…' : 'Sign in to Trading Console'}
              </button>

              {error && (
                <div className="flex items-center gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  <FiHelpCircle className="text-rose-300" />
                  <span>{error}</span>
                </div>
              )}
            </form>

            <div className="mt-8 space-y-4 text-center text-sm text-slate-400">
              <a
                href="https://t.me/GeminiAI_CS"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700/70 px-4 py-2 font-medium text-cyan-200 transition hover:border-cyan-500 hover:text-cyan-100"
              >
                <FiHelpCircle /> Need help? Contact support
              </a>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center justify-center gap-2 text-xs font-medium text-slate-400 transition hover:text-slate-200"
              >
                <FiArrowLeft /> Back to landing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
